/**
 * offline-capture.ts — operator-offline-forms (odd/tasks/operator-offline-forms.md).
 *
 * The field app can save a form with no signal and send it later. Two
 * optional fields on those create bodies make that safe:
 *
 * - `clientRequestId` (S3): a uuid the phone generates once per form. A
 *   resend with the same id returns the record already created — no second
 *   row, no second notification.
 * - `capturedAt` (S2): when the form was filled on the phone. It becomes the
 *   business time (loaded_at, reported_at, requested_at) inside a bounded
 *   window; `created_at` stays the server receive time, for audit.
 *
 * Without either field every endpoint behaves exactly as before (S4).
 */
import { z } from 'zod';

export const ClientRequestIdSchema = z.string().uuid();
export const CapturedAtSchema = z.string().datetime({ offset: true });

/** "no puede ser de más de 72 h atrás" (S2). */
export const CAPTURED_AT_MAX_AGE_HOURS = 72;

/**
 * "no puede ser futura" (S2) — a phone clock a few minutes ahead of the
 * server is not the worker's fault, so up to this much ahead is clamped to
 * the server time instead of rejecting a form the worker cannot fix.
 */
export const CAPTURED_AT_CLOCK_SKEW_MINUTES = 5;

export type CapturedAtRejection = 'future' | 'too_old';

/**
 * Error code of a create rejected for its capturedAt (422). The envelope's
 * `errors` carry `{ field: 'capturedAt', message: CapturedAtRejection }`.
 */
export const CAPTURED_AT_OUT_OF_RANGE = 'CAPTURED_AT_OUT_OF_RANGE';

/**
 * The business time to record for a captured time, or why it is rejected.
 * `undefined` in → `undefined` out: the column default (server time) applies.
 */
export function resolveCapturedAt(
  capturedAt: string | undefined,
  now: Date,
): { ok: true; value: Date | undefined } | { ok: false; reason: CapturedAtRejection } {
  if (capturedAt === undefined) return { ok: true, value: undefined };
  const captured = new Date(capturedAt);
  const ageMs = now.getTime() - captured.getTime();
  if (ageMs > CAPTURED_AT_MAX_AGE_HOURS * 60 * 60 * 1000) return { ok: false, reason: 'too_old' };
  if (ageMs < -CAPTURED_AT_CLOCK_SKEW_MINUTES * 60 * 1000) return { ok: false, reason: 'future' };
  return { ok: true, value: ageMs < 0 ? now : captured };
}
